// The admin tools as tabs — one tap from Message Box to Device Tracking to
// Tuya Testing, in both shells.
//
// ── Keep-alive ─────────────────────────────────────────────────────────────
// A tool opened once STAYS MOUNTED (hidden) while you move between tabs, so the
// Message Box keeps its log and its subscription, Tuya Testing keeps its list,
// and a half-filled Device Admin form is still there when you come back.
// Leaving the admin area altogether (Home, a device) unmounts the lot — a
// hidden console subscribed to `#` for the rest of the session would be a cost
// nobody chose.
//
// ⚠️ THE GATE IS NOT HERE. `screens` arrives from the route table already
// wrapped in that shell's route gate, and each screen checks again inside.
// This file only decides which tabs to DRAW (core/nav/adminTools).
//
// ⚠️ TABS SWITCH WITH `replace`. Back from any tool — the app bar arrow or the
// Android hardware button, both `navigate(-1)` — must leave the admin area for
// wherever the user came from, not walk back through every tab they touched.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { rememberTool, toolForPath, visibleTools } from '../../core/nav/adminTools';

/**
 * @param {'mobile'|'desktop'} shell
 * @param {Object<string, React.ReactNode>} screens  path → gated element
 */
export default function AdminToolsLayout({ shell, screens }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const c = theme.sli || {};
  const { isSuperAdmin, canTuyaTest } = useApp();

  const active = toolForPath(pathname);
  const activePath = active ? active.path : null;
  const tools = visibleTools({ isSuperAdmin, canTuyaTest }, shell)
    .filter((t) => screens[t.path]);

  // Every tool visited in this stay, in the order first opened.
  const [opened, setOpened] = useState(() => (activePath ? [activePath] : []));
  useEffect(() => {
    if (!activePath) return;
    rememberTool(activePath);
    setOpened((o) => (o.includes(activePath) ? o : [...o, activePath]));
  }, [activePath]);

  // The active one is rendered even before the effect above records it, so the
  // first paint is never empty.
  const mounted = activePath && !opened.includes(activePath) ? [...opened, activePath] : opened;
  const tabValue = tools.some((t) => t.path === activePath) ? activePath : false;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Only with somewhere else to go — a bench operator holding just Tuya
          Testing gets the screen without a one-tab strip above it. */}
      {tools.length > 1 && (
        <Tabs
          value={tabValue}
          onChange={(_, path) => navigate(path, { replace: true })}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="inherit"
          sx={{
            flexShrink: 0,
            minHeight: 40,
            bgcolor: c.back,
            color: c.text,
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTabs-indicator': { bgcolor: c.ink || c.text, height: 3 },
            '& .MuiTab-root': {
              minHeight: 40, py: 0.5, px: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 13,
            },
          }}
        >
          {tools.map((t) => <Tab key={t.path} value={t.path} label={t.label} />)}
        </Tabs>
      )}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {mounted.map((path) => (
          <Box
            key={path}
            sx={{
              display: path === activePath ? 'block' : 'none',
              height: '100%',
              overflow: 'auto',
            }}
          >
            {screens[path] || null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
